import { Key } from "@react-types/shared";
import clsx from "clsx";
import {
  ListBoxItem,
  ListBox as RACListBox,
  Selection as RACSelection,
  Text,
} from "react-aria-components";
import { Check } from "react-feather";

import styles from "./list-box.module.css";

export type Selection = "all" | Array<Key>;
export type Item = { id: Key; label: string; key?: Key };

interface ListBoxProps {
  selectedKeys?: Selection;
  onSelectionChange?: (keys: Selection) => void;
  selectionMode?: "single" | "multiple";
  items: Array<Item>;
  "aria-labelledby"?: string;
  onScrolledToBottom?: () => void;
  display?: { maxHeight?: string; maxWidth?: string; minWidth?: string };
  className?: string;
}

function isSelected(item: Item, selectedKeys?: Selection) {
  return (
    selectedKeys && (selectedKeys === "all" || selectedKeys.includes(item.id))
  );
}

const parseChange = (selection: RACSelection) =>
  selection instanceof Set ? Array.from(selection) : selection;

const STYLE_DEFAULTS = {
  maxHeight: "400px",
  maxWidth: "400px",
  minWidth: "240px",
};

function ListBox({
  selectedKeys,
  items,
  selectionMode = "single",
  onSelectionChange,
  "aria-labelledby": ariaLabelledby,
  onScrolledToBottom,
  display,
  className,
}: ListBoxProps) {
  const stylesWithDefaults = {
    ...STYLE_DEFAULTS,
    ...display,
  };
  const options = items.map((item) => {
    const itemSelected = isSelected(item, selectedKeys);
    return (
      <ListBoxItem
        textValue={item.label}
        id={item.id}
        key={item.key ?? item.id}
        className={clsx(styles.menuItem, { [styles.isSelected]: itemSelected })}
      >
        <Text className={styles.itemLabel} slot="label">
          {item.label}
        </Text>
        <Check data-testid="check-icon" className={styles.check} size={18} />
      </ListBoxItem>
    );
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (e.target) {
      const t = e.target as HTMLInputElement;
      const bottom =
        Math.round(t.scrollHeight) - Math.round(t.scrollTop) ===
        Math.round(t.clientHeight);
      if (bottom) {
        if (onScrolledToBottom) {
          onScrolledToBottom();
        }
      }
    }
  };

  return (
    <RACListBox
      aria-labelledby={ariaLabelledby}
      selectedKeys={selectedKeys}
      onSelectionChange={
        onSelectionChange
          ? (keys) => onSelectionChange(parseChange(keys))
          : undefined
      }
      className={clsx(styles.list, display, className)}
      style={stylesWithDefaults}
      selectionMode={selectionMode}
      onScroll={handleScroll}
    >
      {options}
    </RACListBox>
  );
}

export default ListBox;
